import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEye } from "react-icons/fa";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import Grid from '../components/Grid';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function TransportOrders({ onAddTransportOrderClick }) {
  const [transportOrders, setTransportOrders] = useState([]);
  const [selectedTransportOrder, setSelectedTransportOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userLocation, setUserLocation] = useState("");  // Track user's location
  const storedRole = sessionStorage.getItem("userRole");
  const [viewReceivingOrders, setViewReceivingOrders] = useState(false);
  const [showDispatchConfirmation, setShowDispatchConfirmation] = useState(false);
  const [dispatchingOrder, setDispatchingOrder] = useState(null);
  const [checkedTransferOrders, setCheckTransferOrders] = useState(new Map());
  const [showCheckConfirmation, setShowCheckConfirmation] = useState(false);
  const [selectedTransferOrderCode, setSelectedTransferOrderCode] = useState(null);
  const [userId, setUserId] = useState(null);
  const [expandedTransferOrders, setExpandedTransferOrders] = useState({});
  const [transferOrderItems, setTransferOrderItems] = useState({});
  const [username, setUsername] = useState(null);
  const [selectedSearchField, setSelectedSearchField] = useState("transportOrderCode");

  // Fetch user role and location
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        const role = userResponse.data.role;
        setUserRole(role); // Set user role
        setUserLocation(userResponse.data.location); // Set location
        setUserId(userResponse.data._id); // Set user ID
        setUsername(userResponse.data.username); // Set username

        sessionStorage.setItem("userRole", role);
  
        if (role === "Outlet User") {
          setViewReceivingOrders(true); // Ensure Outlet Users only see receiving orders
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Error fetching user data.");
      }
    };
  
    fetchUserData();
  }, []);
  
  // Fetch transport orders
  useEffect(() => {
    const fetchTransportOrders = async () => {
      if (!userRole) return; // Ensure userRole is set before making API call
  
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  
        // Ensure Outlet Users only fetch receiving orders
        const endpoint =
          userRole === "Outlet User" || viewReceivingOrders
            ? `${API_BASE_URL}/transportOrders/receiving`
            : `${API_BASE_URL}/transportOrders`;
  
  
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
  
        const transportOrders = response.data.transportOrders || [];
  
        setTransportOrders(
          transportOrders.map((order) => ({
            id: order._id,
            transportOrderCode: order.transport_order_code || "N/A",
            fromLocation: order.from_location_id?.location_name || "Unknown",
            toLocation: order.to_location_id?.location_name || "Unknown",
            transportOrderDate: new Date(order.transport_order_date).toLocaleDateString("en-GB"),
            created_by: order.created_by?.username || "Unknown",
            driver_name: order.driver_name,
            vehicle_no: order.vehicle_no,
            createdAt: order.createdAt,
            status: order.status,
            transferOrders: order.transfer_order_ids || [],
            salesOrders: order.sales_order_ids || [], 
            dispatchedStockOnLoan: order.stock_on_loan_ids || [],
            originalOrder: order,
          }))
        );
      } catch (error) {
        console.error("Error fetching transport orders:", error);
        toast.error("Error fetching transport orders.");
      }
    };
  
    fetchTransportOrders();
  }, [viewReceivingOrders, userRole]); // Now waits for userRole to be set
  
  
  // Handle transport order click to view details
  const handleTransportOrderClick = (order) => {
    setSelectedTransportOrder(order);
  };

  const handleClosePopup = () => {
    setSelectedTransportOrder(null);
  };

  const sortedTransportOrders = [...transportOrders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const sortedReceivingOrders = [...transportOrders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredTransportOrders = sortedTransportOrders.filter((order) =>
    order.transportOrderCode?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.fromLocation?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.toLocation?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.transportOrderDate?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.created_by?.toLowerCase().includes(searchTerm.toLowerCase())
    || order.status?.toLowerCase().includes(searchTerm.toLowerCase())

  );

  const filteredReceivingOrders = sortedReceivingOrders.filter((order) =>
    order.transferOrders.some(
      (transferOrder) => transferOrder.to_location_id?.location_name === userLocation
    )
  );

  // Handle dispatching of transport order
  const handleDispatchOrder = async () => {
    if (!dispatchingOrder) {
      console.error("No dispatching order found.");
      return;
    }

  
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }
  
      let stockUpdateSuccess = true; // Flag to track stock movement updates

      const productQuantities = {}; // { "product_id_location_id": total_requested_qty }
      const productLocationPairs = [];

      // Collect product-location pairs from sales orders
      for (const salesOrder of dispatchingOrder.salesOrders) {
        for (const item of salesOrder.items) {
          const { product_id, approved_qty } = item;
          const location_id = salesOrder.location_id;

          if (!product_id || !location_id || isNaN(approved_qty) || approved_qty <= 0) continue;

          const key = `${product_id}_${location_id}`;
          productLocationPairs.push({ product_id, location_id });

          if (!productQuantities[key]) {
            productQuantities[key] = 0;
          }
          productQuantities[key] += approved_qty; 
        }
      }

      // Collect product-location pairs from transfer orders
      for (const transferOrder of dispatchingOrder.transferOrders) {
        for (const item of transferOrder.transferOrderItems) {
          const { product_id, qty } = item;
          const location_id = transferOrder.from_location_id;

          if (!product_id || !location_id || isNaN(qty) || qty <= 0) continue;

          const key = `${product_id}_${location_id}`;
          productLocationPairs.push({ product_id, location_id });

          if (!productQuantities[key]) {
            productQuantities[key] = 0;
          }
          productQuantities[key] += qty; 
        }
      }

      // Collect product-location pairs from dispatched stock on loan
      for (const dispatchedStockOnLoan of dispatchingOrder.dispatchedStockOnLoan) {
        for (const item of dispatchedStockOnLoan.dispatchedStockItems) {
          const { product_id, qty } = item;
          const location_id = dispatchedStockOnLoan.from_location_id;

          if (!product_id || !location_id || isNaN(qty) || qty <= 0) continue;

          const key = `${product_id}_${location_id}`;
          productLocationPairs.push({ product_id, location_id });

          if (!productQuantities[key]) {
            productQuantities[key] = 0;
          }
          productQuantities[key] += qty; 
        }
      }


      // Fetch stock levels per product-location pair
      const stockResponses = await axios.post(
        `${API_BASE_URL}/latestStock/getStockByProducts`,
        { product_location_pairs: productLocationPairs },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const currentStockData = stockResponses.data; 
      const stockMap = {}; // { "product_id_location_id": { available_stock, product_name } }

      currentStockData.forEach(({ product_id, location_id, updated_stock_qty, product_name, location_name }) => {
        const key = `${product_id}_${location_id}`;
        stockMap[key] = { available_stock: updated_stock_qty || 0, product_name, location_name};
      });


      // Compare requested quantities with available stock
      for (const key in productQuantities) {
        const [product_id, location_id] = key.split("_");
        const requestedQty = productQuantities[key];
        const availableStock = stockMap[key]?.available_stock || 0;
        const productName = stockMap[key]?.product_name || "Unknown Product";
        const locationName = stockMap[key]?.location_name || "Unknown Location";

        if (requestedQty > availableStock) {
          toast.error(`Insufficient stock for ${productName} at ${locationName}. Requested: ${requestedQty}, Available: ${availableStock}`);
          console.error(`Insufficient stock for product ${product_id} at location ${location_id}.`);
          return;
        }
      }

  

      for (const salesOrder of dispatchingOrder.salesOrders) {
        // Skip cancelled sales orders
        if (salesOrder.status === "Cancelled") {
          continue;
        }

        if (Array.isArray(salesOrder.items)) {
          for (const item of salesOrder.items) {
            const { product_id, approved_qty, unit_price } = item;


            if (!product_id || isNaN(approved_qty) || approved_qty <= 0) {
              console.warn("Invalid sales order item:", item);
              continue;
            }

            const locationId = salesOrder.location_id || "Location ID not found";

            try {
              const stockMovementData = {
                sales_order_code: salesOrder.sales_order_id,
                product_id: product_id,
                location_id: locationId,
                movement_type_id: salesOrder._id,
                movement_type: "Remove",
                movement_name: "sales_order",
                movement_qty: -approved_qty,
                updated_price: unit_price,
                organization_id: salesOrder.organization,
                performed_by: userId,
                user_role: userRole,
              };


              await axios.post(`${API_BASE_URL}/salesOrders/stockmovement`, stockMovementData, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              });
            } catch (error) {
              console.error("Error updating stock movement for sales order:", error.response || error.message);

              if (error.response && error.response.data) {
                const { message, salesOrderId } = error.response.data;
                toast.error(`Error: ${message} for ${salesOrderId}.`);
              } else {
                toast.error("Failed to update stock movement.");
              }

              stockUpdateSuccess = false;
              break;
            }
          }
        }
      }

      // Loop through each transfer order in the transport order
      for (const transferOrder of dispatchingOrder.transferOrders) {
        if (Array.isArray(transferOrder.transferOrderItems)) {
          for (const item of transferOrder.transferOrderItems) {
            const { product_id, qty, updated_price } = item;
  
            if (!product_id || isNaN(qty) || qty <= 0) {
              console.warn("Invalid transfer order item:", item);
              continue; // Skip this item if data is invalid
            }
  
            const locationId = transferOrder.from_location_id; // Source location
  
            try {
              const stockMovementData = {
                transfer_order_code: transferOrder.transfer_order_code,
                product_id: product_id,
                location_id: locationId,
                movement_type_id: transferOrder._id,
                movement_type: "Remove",
                movement_name: "transfer_orders",
                movement_qty: qty, // Negative quantity for stock removal
                updated_price: updated_price,
                organization_id: transferOrder.organization,
                performed_by: transferOrder.created_by,
                user_role: userRole,
              };

              // API call to update stock
              await axios.post(
                `${API_BASE_URL}/transferOrders/stockmovement`,
                stockMovementData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
            } catch (error) {
              console.error("Error updating stock movement for sales order:", error.response || error.message);
      
              // Show a toast with the relevant transfer order ID
              if (error.response && error.response.data) {
                const { message, transferOrderCode } = error.response.data;
                toast.error(`Error: ${message} for ${transferOrderCode}.`);
              } else {
                toast.error("Failed to update stock movement.");
              }
              stockUpdateSuccess = false;
              break; // Stop processing further items if stock update fails
            }
          }
        }
      }

      for (const dispatchStockOnLoan of dispatchingOrder.dispatchedStockOnLoan) {
        if (Array.isArray(dispatchStockOnLoan.dispatchedStockItems)) {
          for (const item of dispatchStockOnLoan.dispatchedStockItems) {
            const { product_id, qty } = item;

  
            if (!product_id || isNaN(qty) || qty <= 0) {
              console.warn("Invalid stock on loan item:", item);
              continue;
            }
  
            const locationId = dispatchStockOnLoan.from_location_id || "Location ID not found";

            try {
              const stockMovementData = {
                dispatched_stock_on_loan_code: dispatchStockOnLoan.dispatched_stock_on_loan_code,
                product_id: product_id,
                location_id: locationId,
                movement_type_id: dispatchStockOnLoan._id,
                movement_type: "Remove",
                movement_name: "dispatch_stock_on_loan",
                movement_qty: qty,
                // updated_price: unit_price,
                organization_id: dispatchStockOnLoan.organization,
                performed_by: dispatchStockOnLoan.created_by,
                user_role: userRole,
              };
  
 
              await axios.post(`${API_BASE_URL}/dispatchedStockOnLoan/stockmovement`, stockMovementData, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              });
            } catch (error) {
              console.error("Error updating stock movement for dispatched stock on loan:", error.response || error.message);
      
              // Show a toast with the relevant sales order ID
              if (error.response && error.response.data) {
                const { message, dispatchedStockOnLoanId } = error.response.data;
                toast.error(`Error: ${message} for ${dispatchedStockOnLoanId}.`);
              } else {
                toast.error("Failed to update stock movement.");
              }
              stockUpdateSuccess = false;
              break;
            }
          }
        }
      }
  
      // If any stock update failed, prevent transport order dispatch
      if (!stockUpdateSuccess) {
        console.error("Stock update failed. Dispatching canceled.");
        toast.error("Stock update failed. Transport order not dispatched.");
        return;
      }
  
      // Proceed to update transport order status only if stock update was successful
      try {
        const transportOrderData = {
          status: "Dispatched",
        };
  
        const response = await axios.put(
          `${API_BASE_URL}/transportOrders/update/${dispatchingOrder.id}`,
          transportOrderData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
  
  
        setTransportOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === dispatchingOrder.id ? { ...order, status: "Dispatched" } : order
          )
        );
  
        toast.success("Transport Order dispatched successfully.");
        setShowDispatchConfirmation(false);
        setDispatchingOrder(null);
        setSelectedTransportOrder(null);
      } catch (error) {
        console.error("Error dispatching transport order:", error.response || error.message);
        toast.error("Failed to dispatch transport order.");
      }
    } catch (error) {
      console.error("Unexpected error in handleDispatchOrder:", error.response || error.message);
      toast.error("Unexpected error occurred while dispatching transport order.");
    }
  };

  const handleCheckBoxClick = (transferOrderCode) => {
    // Prevent unchecking once it's checked
    if (checkedTransferOrders.get(transferOrderCode)) return;
  
    // Store the transfer order code and show confirmation modal
    setSelectedTransferOrderCode(transferOrderCode);
    setShowCheckConfirmation(true);
  };
  
  const confirmCheckBoxChange = () => {
    if (!selectedTransferOrderCode) return;
  
    setCheckTransferOrders((prev) => {
      const newChecked = new Map(prev);
      newChecked.set(selectedTransferOrderCode, true); // Lock checkbox (prevent unchecking)
      
      createReceipt(selectedTransferOrderCode); // Trigger receipt creation
      
      return newChecked;
    });
  
    // Close confirmation modal
    setShowCheckConfirmation(false);
    setSelectedTransferOrderCode(null);
  };    

  const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredTransportOrders : transportOrders;

  if (dataToExport.length === 0) {
    toast.warn("No transport order data to export.");
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Transport Orders");

  const headerRow = [
    "Transport Order Code",
    "From Location",
    "To Location",
    "Date",
    "Status",
    "Created By"
  ];
  worksheet.addRow(headerRow);

  const header = worksheet.getRow(1);
  header.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD966" }, // Light yellow
    };
    cell.font = { bold: true };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  dataToExport.forEach((order) => {
    worksheet.addRow([
      order.transportOrderCode || "N/A",
      order.fromLocation || "Unknown",
      order.toLocation || "Unknown",
      order.transportOrderDate
        ? new Date(order.transportOrderDate).toLocaleDateString("en-GB")
        : "N/A",
      order.status || "N/A",
      order.created_by || "Unknown"
    ]);
  });

  worksheet.columns.forEach((column) => {
    column.width = column.header
      ? column.header.toString().length < 25
        ? 25
        : column.header.toString().length
      : 25;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "transport_orders.xlsx");
};


  const createReceipt = async (transferOrderCode) => {
    try {
  
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }
  
      // Find the transport order containing the specific transfer order
      const transportOrder = transportOrders.find(
        (order) =>
          order.transferOrders.some(
            (transferOrder) => transferOrder.transfer_order_code === transferOrderCode
          )
      );
  
      if (!transportOrder) {
        toast.error("Transport order not found for this transfer order.");
        return;
      }
  
      const transferOrder = transportOrder.transferOrders.find(
        (order) => order.transfer_order_code === transferOrderCode
      ); // Get the specific transfer order
  
      const transportOrderCode = transportOrder.transportOrderCode; 
  
      
      let response;
      try {
        //Perform stock movement for the transfer order item to increase stock
        if (Array.isArray(transferOrder.transferOrderItems)) {
          for (const item of transferOrder.transferOrderItems) {
            const { product_id, qty, updated_price } = item;
  
            if (!product_id || isNaN(qty) || qty <= 0) {
              console.warn("Invalid transfer order item:", item);
              continue; // Skip this item if data is invalid
            }
  
            const locationId = transferOrder.to_location_id; // Source location
            const fromLocationId = transferOrder.from_location_id; // Source location
  
            // Step 3: Perform stock movement to increase stock
            const stockMovementData = {
              transfer_order_code: transferOrder.transfer_order_code,
              product_id: product_id,
              location_id: locationId,
              from_location_id: fromLocationId,
              movement_type_id: transferOrder._id,
              movement_type: "Add", // Increase stock
              movement_name: "transfer_orders",
              movement_qty: qty, // Positive quantity for stock addition
              updated_price: updated_price,
              organization_id: transferOrder.organization,
              performed_by: userId,
              user_role: userRole,
            };
  
  
            // API call to update stock
            await axios.post(
              `${API_BASE_URL}/transferOrders/stockmovement`,
              stockMovementData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
          }

          response = await axios.post(
            `${API_BASE_URL}/transferOrderReceipts/add`,
            {
              transfer_order_code: transferOrderCode,
              transport_order_code: transportOrderCode, // Pass the transportOrderCode here
              created_by: userId,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          ;
          toast.success("Receipt created successfully!");

        }
      } catch (error) {
        console.error("Error creating receipt:", error);
        toast.error("Failed to create receipt.");
        return; // Stop execution if receipt creation fails
      }
  
      // Step 3: Update Transfer Order Status
      try {
        const updateResponse = await axios.put(
          `${API_BASE_URL}/transferOrders/updateStatus/${transferOrderCode}`,
          { status: "Delivered" },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
  
        if (updateResponse.status === 200) {
          toast.success("Transfer order marked as Delivered!");
        } else {
          throw new Error("Unexpected response from API");
        }
      } catch (error) {
        console.error("Error updating transfer order status:", error.response?.data || error.message);
        toast.error("Receipt created, but failed to update transfer order status.");
      }
  
      // Step 4: Update Transport Order Status
      try {
        const newUpdateResponse = await axios.put(
          `${API_BASE_URL}/transportOrders/updateStatus/${transportOrderCode}`, // Use transportOrderCode here
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
  
        if (newUpdateResponse.status === 200) {
          toast.success("Transport order updated!");
        } else {
          throw new Error("Unexpected response from API");
        }
      } catch (error) {
        console.error("Error updating transport order status:", error.response?.data || error.message);
        toast.error("Receipt created, but failed to update transport order status.");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error("An unexpected error occurred.");
    }
  };
  
  const handleExpandClick = async (transferOrderCode) => {
    // Before toggling, check the current expanded state
    const isExpanded = expandedTransferOrders[transferOrderCode] || false;
  
    // Toggle expansion
    setExpandedTransferOrders((prev) => ({
      ...prev,
      [transferOrderCode]: !isExpanded, // Toggle based on the current state
    }));
  
    // If not already expanded, fetch transfer order details
    if (!isExpanded) {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }
  

        const response = await axios.get(
          `${API_BASE_URL}/transferOrders/${transferOrderCode}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
  
        const detailedOrder = response.data.transferOrder;
  
        // Format transfer order items
        const formattedProducts = detailedOrder.transferOrderItems.map((item) => ({
          id: item.product_id._id,
          product_name: item.product_id.product_name,
          product_image: item.product_id.product_image,
          product_code: item.product_id.product_code,
          quantity: item.qty,
          unit: item.product_id.unit?.unit_name,
        }));
  
  
        // Store the detailed order data for this transfer order
        setTransferOrderItems((prevItems) => ({
          ...prevItems,
          [transferOrderCode]: formattedProducts,
        }));
  
      } catch (error) {
        console.error("Error fetching transfer order details:", error.message);
        toast.error("Error fetching transfer order details.");
      }
    }
  };  
  
  useEffect(() => {
    if (selectedTransportOrder) {
    }
  }, [selectedTransportOrder]);

  // DataGrid columns
  const columns = [
    { field: "transportOrderCode", headerName: "Transport Order Code", flex: 1 },
    { field: "transportOrderDate", headerName: "Transport Order Date", flex: 1 },
    { field: "fromLocation", headerName: "Source Location", flex: 1 },
    { field: "toLocation", headerName: "Destination Location", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
    
        // Map statuses to classes
        const statusClassMap = {
          "Dispatched": "bg-[#FBC02D] text-white",
          "Drafted": "bg-[#8BC34A] text-white",
        };
    
        // Get the class based on status or default to a neutral color
        const statusClass = statusClassMap[status] || "bg-[#90A4AE] text-white"; // Default class if status not found
    
        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    { field: "created_by", headerName: "Created By", flex: 1 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <button
          onClick={() => handleTransportOrderClick(params.row)}
          className="bg-[#FBC02D] text-white p-2 flex items-center justify-center rounded"
          style={{ width: "30px", height: "30px" }}
        >
          <FaEye className="text-white" />
        </button>
      ),
    },
  ];
  
  // Merging normal rows + expanded rows dynamically
  const mergedTransferOrderRows = selectedTransportOrder?.transferOrders.flatMap((order) => {
    const expanded = expandedTransferOrders[order.transfer_order_code] || false;
    const baseRow = {
      id: order._id,
      transfer_order_code: order.transfer_order_code || "N/A",
      isParent: true, // Mark parent row
    };
  
    if (!expanded) return [baseRow]; // If not expanded, return only the main row
  
    // When expanded, include child rows for each item
    const itemRows = (transferOrderItems[order.transfer_order_code] || []).map((item) => ({
      id: `${order._id}-${item.id}`, // Unique ID for sub-rows
      product_image: item.product_image,
      product_name: item.product_name,
      quantity: item.quantity,
      unit: item.unit,
      isChild: true, // Mark child row
    }));
  
    return [baseRow, ...itemRows]; // Merge parent row + expanded item rows
  });
  
  // Define columns for both normal and expanded rows
  const dynamicTransferOrderColumns = [
    {
      field: "transfer_order_code",
      headerName: "Transfer Order Code",
      width: 200,
      renderCell: (params) => {
        if (params.row.isChild) return null; // Hide for child rows
        return params.value;
      },
    },
    ...(viewReceivingOrders
      ? [
          {
            field: "expand",
            headerName: "",
            width: 50,
            renderCell: (params) => {
              if (params.row.isChild) return null; // Hide for child rows
              const isExpanded = expandedTransferOrders[params.row.transfer_order_code] || false;
              return (
                <button
                  onClick={() => handleExpandClick(params.row.transfer_order_code)}
                  className="text-gray-600 hover:text-gray-900 focus:outline-none"
                >
                  {isExpanded ? "▼" : "▶"}
                </button>
              );
            },
          },
          {
                        field: "product_image",
                        headerName: "Image",
                        width: 150,
                        renderCell: (params) =>
                        params.row.product_image ? (
                          <img
                            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                            alt="Product"
                            className="h-16 w-16 object-cover rounded-md"
                          />
                          ) : (
                            null
                          ),
                      },
          {
            field: "product_name",
            headerName: "Product Name",
            width: 200,
            renderCell: (params) => (params.row.isChild ? <span>{params.value}</span> : null),
          },
          {
            field: "quantity",
            headerName: "Quantity",
            width: 100,
            headerAlign: "right",
            align: "right",
            renderCell: (params) => (params.row.isChild ? <span>{params.value}</span> : null),
          },
          { 
            field: "unit", 
            headerName: "Units", 
            flex: 1, 
            headerAlign: "center", 
            align: "center" 
          },
          {
            field: "accept",
            headerName: "Action",
            width: 150,
            renderCell: (params) => {
              if (params.row.isChild) return null; // Hide for child rows
          
              const isAccepted = checkedTransferOrders.get(params.row.transfer_order_code) || false;
          
              return (
                <button
                  onClick={() => handleCheckBoxClick(params.row.transfer_order_code)}
                  disabled={isAccepted}
                  className={`px-4 py-2 rounded-lg text-white min-w-max whitespace-nowrap flex items-center justify-center ${
                    isAccepted
                      ? "bg-green-500 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-700"
                  }`}
                  style={{ width: "40px", height: "40px" }}
                >
                  {isAccepted ? "Accepted" : "Accept"}
                </button>
              );
            },
          }
        ]
      : []),
  ];
    
  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">
            {viewReceivingOrders ? "Incoming Transport Orders" : "Outgoing Transport Orders"}
          </h1>
          {(userRole === "Company Admin" || userRole === "Farm User") && (
            <button
              onClick={onAddTransportOrderClick}
              className="bg-green-700 text-white px-4 py-2 rounded-lg"
            >
              Add Transport Order
            </button>
          )}
        </div>
        <div className="flex justify-between mb-4">
        {userRole !== "Outlet User" && (
          <button
            onClick={() => setViewReceivingOrders((prev) => !prev)}
            className="bg-[#7dab89] text-white px-4 py-2 rounded-lg"
          >
            {viewReceivingOrders ? "View All Orders" : "View Receiving Orders"}
          </button>
        )}
        </div>
        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="transportOrderCode">Transport Order Code</option>
                <option value="fromLocation">Source Location</option>
                <option value="toLocation">Destination Location</option>
                <option value="transportOrderDate">Date</option>
                <option value="created_by">Created By</option>
                <option value="status">Status</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Transport Orders"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          
          <Grid rows={filteredTransportOrders} columns={columns} getRowId={(row) => row.transportOrderCode} />
        </div>
        {selectedTransportOrder && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <div className="flex flex-row mt-6">
                <h2 className="text-xl font-semibold mb-4">
                  {selectedTransportOrder.transportOrderCode}
                </h2>
                <div className="mb-4 ml-auto">
                  {selectedTransportOrder.status === "Dispatched" ? (
                    <span className="text-green-600 font-bold text-lg">Dispatched!</span>
                  ) : (
                    !viewReceivingOrders && selectedTransportOrder.status === "Drafted" && (
                      <button
                        onClick={() => {
                          setShowDispatchConfirmation(true);
                          setDispatchingOrder(selectedTransportOrder);
                        }}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg text-lg"
                      >
                        Dispatch
                      </button>
                    )
                  )}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Source Location</label>
                <input
                  type="text"
                  value={selectedTransportOrder.fromLocation || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Destination Location</label>
                <input
                  type="text"
                  value={selectedTransportOrder.toLocation || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Driver Name</label>
                <input
                  type="text"
                  value={selectedTransportOrder.driver_name || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Vehicle No</label>
                <input
                  type="text"
                  value={selectedTransportOrder.vehicle_no || ""}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className={`grid ${viewReceivingOrders ? "grid-cols-1" : "grid-cols-3"} gap-4`}>
                {/* Display Transfer Orders */}
                <div className="mb-4">
                  <label className="block text-gray-700 font-bold">Transfer Orders</label>
                  {selectedTransportOrder && selectedTransportOrder.transferOrders && (
                    <>
                    <DataGrid
                      rows={mergedTransferOrderRows} // Use modified rows with expansion
                      columns={dynamicTransferOrderColumns} // Use dynamic columns
                      pageSize={5}
                      autoHeight
                      getRowClassName={(params) =>
                        params.row.isChild ? "bg-gray-50" : "" // Add background color for child rows
                      }
                    />
                    </>
                  )}
                </div>
                {/* Display Sales Orders (Only when not in Receiving Orders View) */}
                {!viewReceivingOrders && (
                    <div className="mb-4">
                    <label className="block text-gray-700 font-bold">Sales Orders</label>
                    <DataGrid
                        rows={selectedTransportOrder.salesOrders.map(order => ({
                        id: order._id,  // Ensure unique id
                        sales_order_code: order.sales_order_id || "N/A",
                        }))}
                        columns={[{ field: "sales_order_code", headerName: "Sales Order Code", width: 200 }]}
                        pageSize={5}
                        autoHeight
                    />
                    </div>
                    
                )}
                {!viewReceivingOrders && (
                    <div className="mb-4">
                    <label className="block text-gray-700 font-bold">Stock On Loan</label>
                    <DataGrid
                        rows={selectedTransportOrder.dispatchedStockOnLoan.map(order => ({
                        id: order._id,  // Ensure unique id
                        dispatched_stock_on_loan_code: order.dispatched_stock_on_loan_code || "N/A",
                        }))}
                        columns={[{ field: "dispatched_stock_on_loan_code", headerName: "Stock On Loan Code", width: 200 }]}
                        pageSize={5}
                        autoHeight
                    />
                    </div>
                    
                )}
                </div>
              {showDispatchConfirmation && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h2 className="text-lg font-semibold mb-4">Confirm Dispatch!</h2>
                    <p>Are you sure you want to Dispatch this transport order?</p>
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={() => setShowDispatchConfirmation(false)}
                        className="mr-4 px-4 py-2 bg-gray-500 text-white rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDispatchOrder}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showCheckConfirmation && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h2 className="text-lg font-semibold mb-4">Confirm Create Receipt?</h2>
                    <p>Are you sure you want to create a receipt for Transfer Order: <b>{selectedTransferOrderCode}</b>?</p>
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={() => setShowCheckConfirmation(false)}
                        className="mr-4 px-4 py-2 bg-gray-500 text-white rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmCheckBoxChange}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransportOrders;
